import { CurrencyPipe, PercentPipe } from '@angular/common';
import { SelectButtonModule } from 'primeng/selectbutton';
import { Component } from '@angular/core';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { StripeHandlerService } from '../../services/stripe-handler.service';
import { Observable, map, take, tap } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { PaymentTypes } from '../../utils/stripe.utils';
import { FormsModule } from '@angular/forms';
import { InputNumberModule } from 'primeng/inputnumber';
import { DropdownModule } from 'primeng/dropdown';
import { MessageService } from 'primeng/api';


interface PricingCard {
  icon: string
  subtext: string
  price: number
  amount: number
  showSavings?: boolean
  popular?: boolean
  priceId: string
}

enum Pricing {
  Enterprise,
  Individual
}

interface Option {
  value: Pricing
  label: string
}

interface CreditPricingCard extends PricingCard {
}

interface EnterprisePricingCard extends PricingCard {
  teamSizeOptions: {
    value: number
    label: string
  }[]
}

interface SubscriptionPricingCard extends PricingCard {
  billingCycle: string
}

@Component({
  selector: 'app-home-pricing',
  standalone: true,
  imports: [
    AsyncPipe,
    CardModule,
    TagModule,
    CurrencyPipe,
    ButtonModule,
    PercentPipe,
    SelectButtonModule,
    FormsModule,
    DropdownModule
  ],
  templateUrl: './home-pricing.component.html',
  styleUrl: './home-pricing.component.scss',
})
export class HomePricingComponent {
  clicked: string | undefined = undefined

  pricingOptions: Option[] = [
    {
      label: "Individual",
      value: Pricing.Individual
    },
    {
      label: "Enterprise",
      value: Pricing.Enterprise
    }
  ]

  currentSelection: Pricing = Pricing.Individual
  pricingEnum = Pricing

  teamSize: Record<string, number> = {}

  creditCards$ = this.stripeService.creditProducts$.pipe(
    map((products: any) => {
      const changed: CreditPricingCard[] = products.map((product: any) => {
        return {
          icon: product.metadata["icon"] as string ?? "",
          subtext: product.description ?? "",
          amount: product.metadata["amount"] as number ?? 0,
          price: (product.prices.at(0)?.unit_amount)/100,
          popular: product.metadata["popular"] === "true",
          showSavings: product.metadata["showSavings"] === "true",
          priceId: (product.prices.at(0)?.id) ?? ""
        }
      })
      return changed
    }),
    map(x => x.sort((a,b) => a.price - b.price)),
  )

  subscriptionCards$ = this.stripeService.subscriptionProducts$.pipe(
    map((products: any) => {
      const changed: SubscriptionPricingCard[] = products.map((product: any) => {
        return {
          icon: product.metadata["icon"] as string ?? "",
          billingCycle: product.metadata["billingCycle"] as string ?? "",
          subtext: product.description ?? "",
          amount: product.metadata["amount"] as number ?? 0,
          price: (product.prices.at(0)?.unit_amount)/100,
          popular: product.metadata["popular"] === "true",
          showSavings: product.metadata["showSavings"] === "true",
          priceId: (product.prices.at(0)?.id) ?? ""
        }
      })
      return changed
    }),
    map(x => x.sort((a,b) => a.price - b.price)),
  )

  enterpriseCards$ = this.stripeService.enterpriseProducts$.pipe(
    map((products: any) => {
      const enterpriseProduct = products[0]
      const changed: EnterprisePricingCard[] = enterpriseProduct.prices.map((price: any) => {
        const minUsers =  Number(price.metadata["minUsers"])
        const maxUsers = Number(price.metadata["maxUsers"])
        return {
          icon: price.metadata["icon"] as string ?? "",
          subtext: price.metadata['description'] ?? "",
          amount: price.metadata['amount'] ?? "",
          price: price.unit_amount / 100,
          popular: price.metadata["popular"] === "true",
          showSavings: price.metadata["showSavings"] === "true",
          priceId: price.id ?? "",
          teamSizeOptions:  new Array((maxUsers - minUsers) + 1).fill(0).map((_, idx) => ({
            value: idx + minUsers,
            label: `${idx + minUsers} Users`
          }))
        }
      })
      return changed
    }),
    map(x => x.sort((a,b) => b.price - a.price )),
  )

  baseCostPerTeamMember$ = this.enterpriseCards$.pipe(
    map(products => {
      const base = products.at(0)
      if (!base) {
        return 0
      }
      return base.price
    }),
  )

  costPerCredit$ = this.creditCards$.pipe(
    map(products => {
      const base = products.at(0)
      if (!base) {
        return
      }
      return base.price / base.amount
    })
  )

  costPerMonth$ = this.subscriptionCards$.pipe(
    map(products => {
      const base = products.at(0)
      if (!base) {
        return
      }
      return (base.price / base.amount) / 3
    })
  )

  purchaseClicked(priceId: string, type: PaymentTypes, quantity?: number): void {
    if (!this.clicked) {
      this.stripeService.createCheckoutSession(priceId, type, quantity)
      this.clicked = priceId
    }
  }

  purchaseEnterpriseClicked(priceId: string, type: PaymentTypes) {
    const amount = this.teamSize[priceId]
    if (!amount) {
      this.messageService.add({severity: 'warn', summary: 'Team Size', detail: `Must select a team size to purchase`});
      return
    }
    this.purchaseClicked(priceId, type, amount)
  }

  constructor(private stripeService: StripeHandlerService, private messageService: MessageService) {}
}
